import { useEffect, useState } from 'react';
import { operatorConsole, OperatorSettings } from '../../lib/api/operatorConsole';
import { previewLaunchUrl } from '../../lib/previewLaunchUrl';
import { Button } from '../../components/ui/Button';
import { CallbackEditor } from './CallbackEditor';
import { CallbackDocsModal } from './CallbackDocsModal';
import { CALLBACK_DOCS } from './callbackDocs';

const IFRAME_ORIGIN = import.meta.env.VITE_IFRAME_ORIGIN || 'http://localhost:7474';

export default function IntegrationPage() {
  const [apiKey, setApiKey] = useState('');
  const [settings, setSettings] = useState<OperatorSettings | null>(null);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [templates, setTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [ipWhitelist, setIpWhitelist] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [callbackPaths, setCallbackPaths] = useState<Record<string, string>>({});
  const [showSecret, setShowSecret] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [iframeUrls, setIframeUrls] = useState<Record<string, string>>({});
  const [disableShipping, setDisableShipping] = useState(false);
  const [playerId, setPlayerId] = useState('');
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [currency, setCurrency] = useState('');
  const [lobbyUrl, setLobbyUrl] = useState('');
  const [depositUrl, setDepositUrl] = useState('');
  const [activeDocKey, setActiveDocKey] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const me = await operatorConsole.me();
        // Owner-only field. This whole page is owner-only, so it is always
        // present in practice — the fallback just keeps the type honest.
        setApiKey(me.operator.api_key ?? '');
        setTemplates(me.operator.available_templates);
        setCurrencies(me.operator.currencies ?? []);
        setCurrency(me.operator.currency ?? me.operator.currencies?.[0] ?? '');

        const s = await operatorConsole.getSettings();
        setSettings(s);
        setIpWhitelist((s.ip_whitelist || []).join('\n'));
        setCallbackUrl(s.callback_base_url || '');
        setCallbackPaths(s.callback_paths || {});
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleRotateSecret = async () => {
    if (!window.confirm('Are you sure? This will invalidate the current secret.')) return;
    try {
      const res = await operatorConsole.rotateSecret();
      setNewSecret(res.api_secret);
      setShowSecret(true);
    } catch {
      alert('Failed to rotate secret');
    }
  };

  const handleSaveIpWhitelist = async () => {
    try {
      const ips = ipWhitelist.split('\n').map((s) => s.trim()).filter(Boolean);
      await operatorConsole.patchSettings({ ip_whitelist: ips });
      alert('IP whitelist saved');
    } catch {
      alert('Failed to save IP whitelist');
    }
  };

  const handleSaveCallbacks = async () => {
    try {
      await operatorConsole.patchSettings({
        callback_base_url: callbackUrl || null,
        callback_paths: callbackPaths,
      });
      alert('Callbacks saved');
    } catch {
      alert('Failed to save callbacks');
    }
  };

  // Launch params shared by Generate / Open. Optional fields go only when
  // filled in, so the preview session carries the same shape a real partner
  // launch (POST /v1/sessions) would.
  const iframeLaunchParams = (template: string) => ({
    template,
    disable_shipping: disableShipping,
    ...(playerId.trim() ? { player_id: playerId.trim() } : {}),
    ...(currency ? { currency } : {}),
    ...(lobbyUrl.trim() ? { lobby_url: lobbyUrl.trim() } : {}),
    ...(depositUrl.trim() ? { deposit_url: depositUrl.trim() } : {}),
  });

  const handleGenerateIframeUrl = async (template: string) => {
    try {
      const res = await operatorConsole.iframeUrl(iframeLaunchParams(template));
      // Composed BEFORE setState: a state-updater callback runs outside this
      // try/catch, so a missing origin would escape as an unhandled error.
      const url = previewLaunchUrl(res);
      setIframeUrls((prev) => ({ ...prev, [template]: url }));
    } catch (e) {
      // previewLaunchUrl throws when this build has no VITE_IFRAME_ORIGIN —
      // that message names the missing knob, so show it rather than swallow it.
      alert(e instanceof Error ? e.message : `Failed to generate URL for ${template}`);
    }
  };

  const handleOpenIframeUrl = async (template: string) => {
    const popup = window.open('', '_blank');
    try {
      const res = await operatorConsole.iframeUrl(iframeLaunchParams(template));
      const url = previewLaunchUrl(res);
      setIframeUrls((prev) => ({ ...prev, [template]: url }));
      if (popup) popup.location.href = url;
    } catch (e) {
      popup?.close();
      alert(e instanceof Error ? e.message : `Failed to generate URL for ${template}`);
    }
  };

  const embedKey = settings?.embed_key?.trim() ?? '';
  const embedKeyPresent = embedKey.length > 0;
  const embedUrl = `${IFRAME_ORIGIN}/?embed_key=${embedKey}`;

  const handleCopyEmbedUrl = async () => {
    try {
      await navigator.clipboard?.writeText(embedUrl);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — still flash the state.
    }
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 1500);
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-2xl font-bold">Integration Settings</h1>

      {/* API Key */}
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="mb-3 text-lg font-semibold">API Key</h2>
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded bg-ui-bg p-2 font-mono text-sm">{apiKey}</code>
          <Button
            onClick={() => navigator.clipboard.writeText(apiKey)}
            className="shrink-0"
          >
            Copy
          </Button>
        </div>
      </div>

      {/* Rotate Secret */}
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="mb-3 text-lg font-semibold">Rotate API Secret</h2>
        <Button onClick={handleRotateSecret} className="mb-3">
          Rotate API Secret
        </Button>
        {showSecret && (
          <div className="rounded border border-red-300 bg-red-50 p-3">
            <p className="mb-2 text-sm font-semibold text-red-900">Your new api_secret (shown once)</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-white p-2 font-mono text-sm text-red-900">{newSecret}</code>
              <Button
                onClick={() => navigator.clipboard.writeText(newSecret)}
                className="shrink-0 px-2 py-1 text-xs"
              >
                Copy
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* IP Whitelist */}
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="mb-3 text-lg font-semibold">IP Whitelist</h2>
        <textarea
          value={ipWhitelist}
          onChange={(e) => setIpWhitelist(e.target.value)}
          placeholder="One IP per line"
          className="mb-3 w-full rounded border border-ui-border bg-ui-bg p-2 font-mono text-sm"
          rows={4}
        />
        <Button onClick={handleSaveIpWhitelist}>Save IP Whitelist</Button>
      </div>

      {/* Callbacks */}
      <div className="rounded-lg border border-ui-border bg-ui-card p-4">
        <h2 className="mb-3 text-lg font-semibold">Callback Configuration</h2>
        <div className="mb-3 space-y-2">
          <label className="block text-sm font-medium">Base URL</label>
          <input
            type="text"
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            placeholder="https://example.com/cb"
            className="w-full rounded border border-ui-border bg-ui-bg p-2 text-sm"
          />
        </div>
        <div className="mb-3">
          <label className="mb-2 block text-sm font-medium">Callback Paths</label>
          <CallbackEditor
            paths={callbackPaths}
            onChange={(key, path) => setCallbackPaths((prev) => ({ ...prev, [key]: path }))}
            onOpenDocs={(key) => setActiveDocKey(key)}
          />
        </div>
        <Button onClick={handleSaveCallbacks}>Save Callbacks</Button>
      </div>

      {/* Iframe URLs */}
      {templates.length > 0 && (
        <div className="rounded-lg border border-ui-border bg-ui-card p-4">
          <h2 className="mb-3 text-lg font-semibold">Iframe URLs</h2>
          <div className="mb-3">
            <label htmlFor="iframe-player-id" className="mb-1 block text-sm font-medium">
              Player ID
            </label>
            <input
              id="iframe-player-id"
              type="text"
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              placeholder="Player ID (required)"
              className="w-full rounded border border-ui-border bg-ui-bg p-2 text-sm"
            />
          </div>
          {currencies.length > 0 && (
            <div className="mb-3">
              <label htmlFor="iframe-currency" className="mb-1 block text-sm font-medium">
                Currency
              </label>
              <select
                id="iframe-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded border border-ui-border bg-ui-bg p-2 text-sm"
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="iframe-lobby-url" className="mb-1 block text-sm font-medium">
              Lobby URL
            </label>
            <input
              id="iframe-lobby-url"
              type="url"
              value={lobbyUrl}
              onChange={(e) => setLobbyUrl(e.target.value)}
              placeholder="https://example.com/lobby (optional)"
              className="w-full rounded border border-ui-border bg-ui-bg p-2 text-sm"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="iframe-deposit-url" className="mb-1 block text-sm font-medium">
              Deposit URL
            </label>
            <input
              id="iframe-deposit-url"
              type="url"
              value={depositUrl}
              onChange={(e) => setDepositUrl(e.target.value)}
              placeholder="https://example.com/cashier (optional)"
              className="w-full rounded border border-ui-border bg-ui-bg p-2 text-sm"
            />
          </div>
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={disableShipping}
              onChange={(e) => setDisableShipping(e.target.checked)}
            />
            Disable shipping
          </label>
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t} className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{t}</span>
                <div className="flex shrink-0 items-center gap-2">
                  <Button onClick={() => handleGenerateIframeUrl(t)} className="px-2 py-1 text-xs">
                    Generate
                  </Button>
                  <Button onClick={() => handleOpenIframeUrl(t)} className="px-2 py-1 text-xs">
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {Object.entries(iframeUrls).length > 0 && (
            <div className="mt-4 space-y-2 border-t border-ui-border pt-4">
              {Object.entries(iframeUrls).map(([template, url]) => (
                <div key={template} className="text-sm">
                  <p className="mb-1 font-medium">{template}</p>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate text-blue-600 underline"
                  >
                    {url}
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Embed mode */}
      {settings?.registration_mode === 'required' && (
        <div className="rounded-lg border border-ui-border bg-ui-card p-4">
          <h2 className="mb-3 text-lg font-semibold">Embed mode</h2>
          <p className="mb-3 text-sm text-ui-text-secondary">
            Players sign up directly in the iframe — no partner backend or session needed.
          </p>
          <input
            type="text"
            readOnly
            value={embedKeyPresent ? embedUrl : ''}
            className="mb-3 w-full rounded border border-ui-border bg-ui-bg p-2 font-mono text-sm"
          />
          <div className="flex items-center gap-2">
            <Button onClick={handleCopyEmbedUrl} disabled={!embedKeyPresent}>
              {embedCopied ? 'Copied!' : 'Copy URL'}
            </Button>
            <Button
              onClick={() => window.open(embedUrl, '_blank', 'noopener,noreferrer')}
              disabled={!embedKeyPresent}
            >
              Open iframe
            </Button>
          </div>
          {!embedKeyPresent && (
            <p className="mt-3 text-sm text-ui-text-secondary">
              Embed key not set yet — set registration to Required (it is generated automatically).
            </p>
          )}
        </div>
      )}

      {/* Callback Docs Modal */}
      <CallbackDocsModal
        doc={activeDocKey ? CALLBACK_DOCS.find((d) => d.key === activeDocKey) || null : null}
        onClose={() => setActiveDocKey(null)}
      />
    </div>
  );
}
